import React from 'react';

import { Box } from '@mui/material';
import { compact, lowerCase } from 'lodash';
import logger from 'logger';
import { GetServerSideProps } from 'next';
import Layout from 'src/components/Layout';
import MealDetailsContent from 'src/components/MealDetailsModal/MealDetailsContent';
import MealDetailsHeader from 'src/components/MealDetailsModal/MealDetailsHeader';
import ConfigContext from 'src/context/config';
import { GET_VARIANTS } from 'src/graphql-operations/catalog-service/catalogs.query';
import useNonNullContext from 'src/hooks/useNonNullContext';
import {
  MacroNutrients,
  Tag,
  VariantsQuery,
} from 'src/ts/@types/generated/graphql/catalog-service/graphql';
import { LegacyAllergenCategories } from 'src/ts/enums/ingredientsToAvoid.enums';
import { TagCategories, Tags } from 'src/ts/enums/tags.enums';
import { ClientNames, initializeApollo } from 'src/utils/apolloClient';
import { getDietaryTemplatesFromTConfig } from 'src/utils/catalog-items';

interface MealDetailsPageContent {
  title: string;
  subtitle: string;
  description: string;
  designer: string;
  imageUrl: string;
  macroNutrients: MacroNutrients;
  publicIngredientsCopy: string;
  nutritionCaveat: string | null;
  containsAllergensCopy: string | null;
  kosherTagDisplayName: string | null;
  isNaeProteinSourcingStandard: boolean;
  shelfLifeTagId: number | null;
  servingsTag: { displayName: string } | null;
  dietaryTemplateTags: Tag[];
}

interface Props {
  mealDetails: MealDetailsPageContent;
}

const Meals: React.FC<Props> = ({ mealDetails }) => {
  const config = useNonNullContext(ConfigContext);

  const dietaryTemplateDisplayNames = compact(
    getDietaryTemplatesFromTConfig(
      mealDetails.dietaryTemplateTags,
      config.visibleTagFilters ?? [],
      'funnel',
    ),
  ).map((tagFilter) => tagFilter.displayName);

  return (
    <Layout>
      <Box
        sx={{
          width: '100%',
          maxWidth: '900px',
          margin: '2rem auto',
          backgroundColor: 'background.paper',
          border: '1px solid #DAD4BC',
          borderRadius: '2px',
        }}
      >
        <MealDetailsHeader
          title={mealDetails.title}
          subtitle={mealDetails.subtitle}
          designer={mealDetails.designer}
        />
        <MealDetailsContent
          title={mealDetails.title}
          subtitle={mealDetails.subtitle}
          description={mealDetails.description}
          macroNutrients={mealDetails.macroNutrients}
          servingsTag={mealDetails.servingsTag}
          nutritionCaveat={mealDetails.nutritionCaveat}
          publicIngredientsCopy={mealDetails.publicIngredientsCopy}
          containsAllergensCopy={mealDetails.containsAllergensCopy}
          imageUrl={mealDetails.imageUrl}
          dietaryTemplateDisplayNames={dietaryTemplateDisplayNames}
          isNaeProteinSourcingStandard={
            mealDetails.isNaeProteinSourcingStandard
          }
          shelfLifeTagId={mealDetails.shelfLifeTagId ?? undefined}
          kosherTagDisplayName={mealDetails.kosherTagDisplayName}
        />
      </Box>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const headers = ctx.req.headers;
  const apolloClient = initializeApollo({ headers });

  const mealId = ctx.query.id as string;

  const variantsRes = await apolloClient
    .query<VariantsQuery>({
      query: GET_VARIANTS,
      variables: {
        variantsInput: {
          gardenMealIds: [Number(mealId)],
        },
      },
      context: {
        clientName: ClientNames.CATALOG_SERVICE_GRAPH_ABSOLUTE_URL,
        headers: { 'territory-api-key': process.env.TERRITORY_API_KEY },
      },
      errorPolicy: 'all',
    })
    .catch((err) => {
      logger.error({
        noticable: err,
        msg: 'Failed to fetch meal details',
        extra: {
          context: {
            mealId,
          },
        },
      });
    });

  if (
    variantsRes &&
    variantsRes.data?.variants &&
    variantsRes.data.variants.length > 0
  ) {
    const variant = variantsRes.data.variants.at(0);
    //we can't support rendering add on variants on this page atm.
    if (!variant || variant.__typename === 'AddOnVariant') {
      return {
        notFound: true,
      };
    } else if (variant.__typename === 'MealVariant') {
      const shelfLifeTagId = variant.tags.find(
        (tag) => tag?.category.id === TagCategories.SHELF_LIFE,
      )?.id;

      const servingsTag =
        variant.tags.find(
          (tag) => tag?.category.id === TagCategories.SERVINGS,
        ) ?? null;

      /**
       * We ONLY want to show information about sourcing standards, if there's no
       * antibiotics AND there's animal proteins on the meal.
       */
      const isNaeProteinSourcingStandard = (() => {
        const hasAntibioticsTag = variant.tags.some(
          (tag) => tag?.id === Tags.NAE_PROTEIN_SOURCING_STANDARD,
        );
        const hasAnimalProteins = variant.ingredientsToAvoid.some(
          (ingredientToAvoid) =>
            ingredientToAvoid.category.legacyAllergenCategoryId ===
            LegacyAllergenCategories.ANIMAL_PROTEINS,
        );

        return hasAntibioticsTag && hasAnimalProteins;
      })();

      // Check for the kosher tag and grab its display name if it exists
      const kosherTagDisplayName =
        variant.tags.find(
          (tag) => tag && tag.category.id === TagCategories.KOSHER,
        )?.displayName ?? null;

      const dietaryTemplateTags = compact(
        variant.tags.filter(
          (tag) => tag?.category.id === TagCategories.DIETARY_TEMPLATE,
        ),
      ) as Tag[];

      const allergens = compact(
        variant.ingredientsToAvoid.map((ingredientToAvoid) => {
          if (ingredientToAvoid.isAllergen) {
            return lowerCase(ingredientToAvoid.displayName);
          }
        }),
      ).join(', ');

      const mealDetails: MealDetailsPageContent = {
        title: variant.product.title,
        subtitle: variant.product.subtitle ?? '',
        description: variant.product.description ?? '',
        designer: variant.product.designer.name,
        imageUrl: variant.product.imageUrl as string,
        macroNutrients: variant.macroNutrients,
        publicIngredientsCopy: variant.publicIngredientsCopy,
        nutritionCaveat: variant.nutritionCaveat ?? null,
        containsAllergensCopy: allergens ? allergens : null,
        kosherTagDisplayName,
        isNaeProteinSourcingStandard,
        shelfLifeTagId: shelfLifeTagId ?? null,
        servingsTag,
        dietaryTemplateTags,
      };

      return { props: { mealDetails } };
    }
  }
  // If no meal returned or we get to this part somehow, serve 404 page.
  return {
    notFound: true,
  };
};

export default Meals;
